/* eslint-disable camelcase */
import { type FC, useState, useEffect, Fragment } from "react";
import { axiosPost } from "@root/shared/utils/axiosForRails";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { type AxiosError } from "axios";
import { Cassette } from "./src/cassette";
import { type CassetteProps } from "./src/cassette/src/cassette_props";

type FormParams = Record<string, string | number>;

type Params = {
  pinpoint_high_rank_works_ids?: string[];
  jobs_ids?: string[];
  form?: FormParams;
  site_id?: number;
  query_string?: string;
  controller_name?: string;
  action_name?: string;
  params_id?: string;
  is_recommend?: boolean;
};

type Props = {
  fetchUrl: string;
  dummyImgPath: string;
  login: boolean;
  isSp: boolean;
  keepedFlags: boolean[];
  cassetteType?: string;
  showAwardsLabelFlag?: boolean;
  isContractRunning: boolean;
  params?: Params;
};

type ResponseProps = {
  data: {
    data?: CassetteProps["cassetteHash"][];
  };
};

/**
 * form[...] のネスト解除だけ行い、
 * 配列はそのまま string[] として返す
 */
function flattenForRails(params: Params): Record<string, string | string[]> {
  const result: Record<string, string | string[]> = {};

  // params の各キー・値をひとつずつ処理
  Object.entries(params).forEach(([key, val]) => {
    // ① form オブジェクトだけネスト解除
    if (key === "form" && typeof val === "object" && val !== null) {
      Object.entries(val).forEach(([nestedKey, nestedVal]) => {
        // form[foo]=bar 形式のキーを作成
        result[`form[${nestedKey}]`] = String(nestedVal);
      });

      // ② 配列は string[] に変換してそのまま
    } else if (Array.isArray(val)) {
      // key はそのまま、値だけ文字列化
      result[key] = val.map((x) => String(x));

      // ③ null/undefined でなければ文字列化
    } else if (val != null) {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string -- クエリ値はプリミティブ想定（型上は unknown のため検知される）
      result[key] = String(val);
    }
  });

  return result;
}

export const Cassettes: FC<Props> = ({
  fetchUrl,
  dummyImgPath,
  login,
  isSp,
  keepedFlags,
  cassetteType,
  showAwardsLabelFlag,
  isContractRunning,
  params,
}) => {
  const [cassettes, setCassettes] = useState<CassetteProps["cassetteHash"][]>([]);

  useEffect(() => {
    (async () => {
      const bodyParams = flattenForRails(params || {});

      await axiosPost
        .post(fetchUrl, bodyParams)
        .then(({ data }: ResponseProps) => {
          if (data.data) {
            setCassettes(data.data);
          }
        })
        .catch((error: AxiosError) => {
          sendErrorLog(new Error(error.message || "求人カセットのデータを取得できませんでした"));
        });
    })();
  }, [fetchUrl, params]);

  return (
    <>
      {cassettes.map((cassette, index) => (
        <Fragment key={cassette.workCode}>
          <Cassette
            cassetteHash={{
              ...cassette,
              searchEvents: {
                ...cassette.searchEvents,
                cassetteEvent: {
                  ...cassette.searchEvents.cassetteEvent,
                  cassetteSearchListRank: index + 1,
                },
              },
            }}
            dummyImgPath={dummyImgPath}
            login={login}
            isSp={isSp}
            keepedFlag={keepedFlags[index]}
            cassetteType={cassetteType}
            showAwardsLabelFlag={showAwardsLabelFlag}
            isContractRunning={isContractRunning}
          />
        </Fragment>
      ))}
    </>
  );
};
